import React from 'react';
import { motion } from 'framer-motion';
import { Play } from 'lucide-react';
import GrainOverlay from './GrainOverlay';

const Hero: React.FC = () => {
  const handleScrollToProducts = () => {
    const element = document.querySelector('#products');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleScrollToDemo = () => {
    const element = document.querySelector('#demo');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // ECG waveform path
  const ecgPath =
    'M0,50 L20,50 ' +
    'Q25,50 28,44 L30,50 ' +
    'L45,50 L48,50 L50,20 L52,75 L54,35 L56,50 ' +
    'L65,50 Q70,50 73,40 L76,50 ' +
    'L95,50 ' +
    'Q100,50 103,44 L105,50 ' +
    'L120,50 L123,50 L125,20 L127,75 L129,35 L131,50 ' +
    'L140,50 Q145,50 148,40 L151,50 ' +
    'L170,50 ' +
    'Q175,50 178,44 L180,50 ' +
    'L195,50 L198,50 L200,20 L202,75 L204,35 L206,50 ' +
    'L215,50 Q220,50 223,40 L226,50 ' +
    'L245,50 ' +
    'Q250,50 253,44 L255,50 ' +
    'L270,50 L273,50 L275,20 L277,75 L279,35 L281,50 ' +
    'L290,50 Q295,50 298,40 L301,50 ' +
    'L320,50';

  // Floating micro-elements configuration
  const floatingElements = [
    {
      type: 'pill' as const,
      className: 'w-7 h-2.5 rounded-full bg-primary-400/40 dark:bg-primary-500/20',
      position: 'top-[18%] left-[8%]',
      animate: { y: [0, -10, 0], rotate: [0, 15, 0] },
      duration: 5,
      delay: 0,
    },
    {
      type: 'plus' as const,
      className: 'text-primary-400/50 dark:text-primary-500/30 text-xl font-light select-none',
      position: 'top-[12%] right-[12%]',
      animate: { y: [0, -8, 0], opacity: [0.5, 0.8, 0.5] },
      duration: 4,
      delay: 1.2,
    },
    {
      type: 'pill' as const,
      className: 'w-5 h-2 rounded-full bg-primary-500/35 dark:bg-primary-400/15',
      position: 'bottom-[22%] right-[6%]',
      animate: { y: [0, -12, 0], rotate: [0, -20, 0] },
      duration: 6,
      delay: 0.8,
    },
    {
      type: 'plus' as const,
      className: 'text-primary-500/45 dark:text-primary-500/25 text-lg font-light select-none',
      position: 'bottom-[28%] left-[5%]',
      animate: { y: [0, -6, 0], opacity: [0.45, 0.7, 0.45] },
      duration: 5.5,
      delay: 2,
    },
    {
      type: 'pill' as const,
      className: 'w-4 h-1.5 rounded-full bg-primary-400/30 dark:bg-primary-600/20',
      position: 'top-[50%] right-[3%]',
      animate: { y: [0, -9, 0], rotate: [0, 25, 0] },
      duration: 7,
      delay: 1.5,
    },
    {
      type: 'plus' as const,
      className: 'text-primary-400/40 dark:text-primary-500/20 text-base font-light select-none',
      position: 'top-[35%] left-[3%]',
      animate: { y: [0, -7, 0], opacity: [0.4, 0.65, 0.4] },
      duration: 6,
      delay: 3,
    },
  ];

  return (
    <section id="home" className="min-h-screen flex items-center pt-24 pb-12 relative overflow-hidden">
      {/* ===== Grain Texture — fades out at bottom into next section ===== */}
      <GrainOverlay fade="bottom" />
      {/* White wash at bottom edge to blend into Products section */}
      <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-b from-transparent to-white/60 dark:to-dark-900/60 pointer-events-none" style={{ zIndex: 1 }} />

      {/* ===== Background Bio Decoration Layer ===== */}
      <div className="absolute inset-0 pointer-events-none" style={{ zIndex: 0 }}>
        {/* ===== Central Microscope Lens / Petri Dish ===== */}
        <div className="absolute top-1/2 left-1/2 -translate-y-1/2 translate-x-[-20%] lg:translate-x-[5%] w-[340px] h-[340px] md:w-[420px] md:h-[420px] lg:w-[500px] lg:h-[500px]">
          {/* Outer ring */}
          <div className="absolute inset-0 rounded-full border-2 border-primary-400/40 dark:border-primary-600/30 bg-primary-100/15 dark:bg-primary-900/10" />

          {/* Subtle radial glow inside the lens */}
          <div
            className="absolute inset-0 rounded-full"
            style={{
              background: 'radial-gradient(circle at center, rgba(26,36,54,0.2) 0%, transparent 70%)',
            }}
          />

          {/* Crosshair — horizontal */}
          <div className="absolute top-1/2 left-0 w-full h-px bg-primary-500/25 dark:bg-primary-500/15" />
          {/* Crosshair — vertical */}
          <div className="absolute left-1/2 top-0 h-full w-px bg-primary-500/25 dark:bg-primary-500/15" />

          {/* Biological blobs — drift only, no scaling */}
          {/* XL */}
          <motion.div
            className="absolute top-[14%] left-[14%] w-28 h-28 rounded-[40%_60%_55%_45%/50%_40%_60%_50%] bg-dark-800/40 dark:bg-dark-300/20"
            animate={{ x: [0, 30, -15, 20, -10, 0], y: [0, -20, 18, -12, 8, 0], rotate: [0, 10, -8, 5, -3, 0] }}
            transition={{ duration: 20, repeat: Infinity, ease: 'easeInOut' }}
          />
          {/* Large */}
          <motion.div
            className="absolute bottom-[16%] right-[14%] w-20 h-20 rounded-[50%_50%_45%_55%/45%_55%_50%_50%] bg-dark-800/35 dark:bg-dark-300/16"
            animate={{ x: [0, -25, 20, -14, 8, 0], y: [0, 24, -18, 15, -8, 0], rotate: [0, -12, 10, -6, 4, 0] }}
            transition={{ duration: 18, repeat: Infinity, ease: 'easeInOut', delay: 1 }}
          />
          {/* Medium */}
          <motion.div
            className="absolute top-[40%] right-[18%] w-14 h-14 rounded-[45%_55%_50%_50%/55%_45%_50%_50%] bg-dark-800/30 dark:bg-dark-300/14"
            animate={{ x: [0, 20, -22, 12, -8, 0], y: [0, 16, -14, 20, -10, 0], rotate: [0, 18, -14, 8, -5, 0] }}
            transition={{ duration: 15, repeat: Infinity, ease: 'easeInOut', delay: 2.5 }}
          />
          {/* Small */}
          <motion.div
            className="absolute top-[62%] left-[22%] w-10 h-10 rounded-[50%_45%_55%_50%/45%_50%_50%_55%] bg-dark-800/35 dark:bg-dark-300/16"
            animate={{ x: [0, -18, 15, -10, 6, 0], y: [0, 14, -20, 10, -6, 0], rotate: [0, -16, 12, -8, 5, 0] }}
            transition={{ duration: 13, repeat: Infinity, ease: 'easeInOut', delay: 3.5 }}
          />
          {/* XS */}
          <motion.div
            className="absolute top-[28%] left-[45%] w-7 h-7 rounded-full bg-dark-800/35 dark:bg-dark-300/16"
            animate={{ x: [0, 14, -16, 10, -6, 0], y: [0, -16, 12, -8, 14, 0] }}
            transition={{ duration: 11, repeat: Infinity, ease: 'easeInOut', delay: 4.5 }}
          />
          {/* Tiny A */}
          <motion.div
            className="absolute top-[72%] right-[32%] w-5 h-5 rounded-full bg-dark-800/40 dark:bg-dark-300/18"
            animate={{ x: [0, -12, 14, -8, 5, 0], y: [0, 10, -14, 12, -6, 0] }}
            transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut', delay: 2 }}
          />
          {/* Tiny B */}
          <motion.div
            className="absolute top-[50%] left-[50%] w-4 h-4 rounded-full bg-dark-800/35 dark:bg-dark-300/16"
            animate={{ x: [0, 10, -12, 8, -5, 0], y: [0, -10, 8, -6, 10, 0] }}
            transition={{ duration: 9, repeat: Infinity, ease: 'easeInOut', delay: 5.5 }}
          />

          {/* Outer ring pulse */}
          <motion.div
            className="absolute inset-0 rounded-full border border-primary-400/15 dark:border-primary-500/8"
            animate={{ scale: [1, 1.06, 1], opacity: [0.5, 0.2, 0.5] }}
            transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut' }}
          />
        </div>

        {/* ===== ECG / Heartbeat Line ===== */}
        <motion.svg
          className="absolute top-[58%] left-0 w-full h-28 overflow-visible"
          viewBox="0 0 320 100"
          preserveAspectRatio="none"
          fill="none"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1.2, delay: 0.8 }}
        >
          <defs>
            <linearGradient id="ecg-fade" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="rgba(26,36,54,0)" />
              <stop offset="8%" stopColor="rgba(26,36,54,0.18)" />
              <stop offset="50%" stopColor="rgba(26,36,54,0.22)" />
              <stop offset="92%" stopColor="rgba(26,36,54,0.18)" />
              <stop offset="100%" stopColor="rgba(26,36,54,0)" />
            </linearGradient>
            <linearGradient id="ecg-fade-static" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="rgba(26,36,54,0)" />
              <stop offset="8%" stopColor="rgba(26,36,54,0.08)" />
              <stop offset="50%" stopColor="rgba(26,36,54,0.10)" />
              <stop offset="92%" stopColor="rgba(26,36,54,0.08)" />
              <stop offset="100%" stopColor="rgba(26,36,54,0)" />
            </linearGradient>
          </defs>
          <path
            d={ecgPath}
            stroke="url(#ecg-fade-static)"
            strokeWidth={0.75}
            strokeLinecap="round"
            strokeLinejoin="round"
            fill="none"
          />
          <motion.path
            d={ecgPath}
            stroke="url(#ecg-fade)"
            strokeWidth={0.75}
            strokeLinecap="round"
            strokeLinejoin="round"
            fill="none"
            initial={{ pathLength: 0 }}
            animate={{ pathLength: 1 }}
            transition={{
              duration: 3.5,
              delay: 1,
              ease: 'easeInOut',
              repeat: Infinity,
              repeatDelay: 2,
            }}
          />
        </motion.svg>

        {/* ===== Floating Micro-Elements ===== */}
        {floatingElements.map((el, index) => (
          <motion.div
            key={index}
            className={`absolute ${el.position}`}
            animate={el.animate}
            transition={{
              duration: el.duration,
              repeat: Infinity,
              ease: 'easeInOut',
              delay: el.delay,
            }}
          >
            {el.type === 'pill' ? (
              <div className={el.className} />
            ) : (
              <span className={el.className}>+</span>
            )}
          </motion.div>
        ))}
      </div>

      {/* ===== Foreground Content ===== */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 w-full relative" style={{ zIndex: 1 }}>
        <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-8">
          {/* Text Content */}
          <motion.div
            className="flex-[3] text-center lg:text-left"
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            {/* Label */}
            <motion.span
              className="inline-block text-sm font-bold uppercase tracking-widest text-secondary-500 mb-4"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              Healthcare Solutions
            </motion.span>

            <motion.h1
              className="font-display text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              <span className="text-dark-900 dark:text-white block">Smart Software</span>
              <span className="text-dark-900 dark:text-dark-100">for Healthcare Professionals</span>
            </motion.h1>

            <motion.p
              className="text-lg md:text-xl text-dark-500 dark:text-dark-300 mb-8 leading-relaxed max-w-2xl"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
            >
              Empowering medical practices, veterinary clinics, pharmacies, physiotherapy clinics, and bioscience teams with intelligent,
              AI-powered solutions that simplify workflows and deliver actionable insights.
            </motion.p>

            <motion.div
              className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.8 }}
            >
              <motion.button
                onClick={handleScrollToProducts}
                className="btn-primary px-8 py-4 text-lg"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Explore Products
              </motion.button>

              <motion.button
                onClick={handleScrollToDemo}
                className="btn-secondary flex items-center justify-center gap-2 px-8 py-4 text-lg"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Play className="w-5 h-5" />
                Watch Demo
              </motion.button>
            </motion.div>
          </motion.div>

          {/* Spacer */}
          <div className="hidden lg:block flex-[2]" />
        </div>
      </div>
    </section>
  );
};

export default Hero;
